<template>
  <ConfirmModal
    ref="modalRef"
    :title="title"
    :description="description"
    confirm-label="Change role"
    width="30rem"
    :loading="isSubmitting"
    @confirm="confirm"
  />
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { computed, ref } from 'vue';

import { ConfirmModal, useAlertsStore } from '@tallo/design-system';
import {
  CompanyRole,
  CompanyTeamMemberDto,
  teamMemberName as nameOf,
  useChangeMemberRole,
} from '@tallo/investor/company';
import { accessRequestsToAnswerQueryKey } from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

const modalRef = ref<InstanceType<typeof ConfirmModal>>();
const member = ref<CompanyTeamMemberDto>();
const alertsStore = useAlertsStore();
const changeMemberRole = useChangeMemberRole();
const queryCache = useQueryCache();
const isSubmitting = changeMemberRole.isLoading;

const nextRole = computed(() => (member.value?.role === CompanyRole.ADMIN ? CompanyRole.MEMBER : CompanyRole.ADMIN));
const name = computed(() => (member.value ? nameOf(member.value) : ''));

const title = computed(() =>
  nextRole.value === CompanyRole.ADMIN ? `Make ${name.value} an admin?` : `Make ${name.value} a member?`,
);

const description = computed(() =>
  nextRole.value === CompanyRole.ADMIN
    ? 'Admins change any property, run every portfolio and manage the team. The portfolios they own stay theirs. It takes effect right away.'
    : "They'll still see the whole company, but can only change the properties they manage or collaborate on and the portfolios they own. It takes effect right away.",
);

function open(target: CompanyTeamMemberDto) {
  member.value = target;
  modalRef.value?.show();
}

async function confirm() {
  if (!member.value) return;

  const role = nextRole.value;

  try {
    await changeMemberRole.mutateAsync({ member: member.value, role });
    // An admin works every unit, so a promotion answers the requests they had open
    queryCache.invalidateQueries({ key: [accessRequestsToAnswerQueryKey] }).catch(() => undefined);
    modalRef.value?.close();
    alertsStore.success(
      `<b>${escapeHtml(name.value)}</b> is now ${role === CompanyRole.ADMIN ? 'an admin' : 'a member'}`,
    );
  } catch {
    // Already reported by the mutation's toast; the dialog stays open for another try
  }
}

defineExpose({ open });
</script>
